import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useTranslation,
  TranslationProvider,
  useLocale,
  Button,
  Icons,
} from "rujira.ui";
import { PageIntro } from "../common/components/PageIntro";
import { getStaticProductSeo, ProductSeoHelmet } from "../seo";
import {
  assetToTradeUrlSegment,
  canonicalChainFor,
} from "../services/assetUrl";
import { Leaderboard } from "./AlgorithmicLeaderboard";
import {
  AlgorithmicPairPicker,
  DEFAULT_PAIR_KEY,
  SelectedPairAssets,
} from "./components/AlgorithmicPairPicker";
import { ALL_PAIRS } from "./components/PairSelect";

const migrateLegacyPairAsset = (asset: string): string => {
  const [symbol, chain] = asset.split(".");
  if (!symbol || !chain) return asset;
  return chain === canonicalChainFor(symbol) ? symbol : `${symbol}.${chain}`;
};

const migratePairParam = (pair: string | null): string | null => {
  if (!pair?.includes("/")) return pair;

  const [base, quote, ...extra] = pair.split("/");
  if (!base || !quote || extra.length > 0) return pair;

  return `${migrateLegacyPairAsset(base)}-${migrateLegacyPairAsset(quote)}`;
};

export function Algorithmic() {
  const { locale } = useLocale();

  return (
    <TranslationProvider namespace="algorithmic">
      <ProductSeoHelmet
        seo={getStaticProductSeo("/automated-trading", locale)}
      />
      <AlgorithmicContent />
    </TranslationProvider>
  );
}

function AlgorithmicContent() {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("algorithmic");
  const { toRoot } = useLocale();
  const navigate = useNavigate();
  const Description = t("description");
  const [searchParams, setSearchParams] = useSearchParams();
  const rawUrlPair = searchParams.get("pair");
  const urlPair = migratePairParam(rawUrlPair);

  const [selectedPair, setSelectedPair] = useState<SelectedPairAssets>();
  const [pickerPairKey, setPickerPairKey] = useState(
    urlPair ?? DEFAULT_PAIR_KEY
  );
  const leaderboardPair = urlPair ?? ALL_PAIRS;

  useEffect(() => {
    if (rawUrlPair === urlPair) return;
    const nextParams = new URLSearchParams(searchParams);
    if (urlPair) nextParams.set("pair", urlPair);
    else nextParams.delete("pair");
    setSearchParams(nextParams, { replace: true });
  }, [rawUrlPair, urlPair, searchParams, setSearchParams]);

  const handlePairSelect = useCallback(
    (pair: SelectedPairAssets | undefined) => {
      setSelectedPair(pair);
    },
    []
  );

  const handlePickerPairChange = useCallback((pair: string) => {
    setPickerPairKey(pair);
  }, []);

  const handleUnavailablePairKey = useCallback((pair: string) => {
    setPickerPairKey((current) =>
      current === pair ? DEFAULT_PAIR_KEY : current
    );
  }, []);
  const handleLeaderboardPairChange = useCallback(
    (pair: string) => {
      const nextParams = new URLSearchParams(searchParams);
      if (pair === ALL_PAIRS) nextParams.delete("pair");
      else nextParams.set("pair", pair);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const createStrategyTo = selectedPair
    ? toRoot(
        `/trade/${assetToTradeUrlSegment(selectedPair.baseAsset)}/${assetToTradeUrlSegment(selectedPair.quoteAsset)}?type=automated`
      )
    : undefined;

  return (
    <>
      <div className="rujira__main rujira__main--gradient lending">
        <div className="rujira__inner--pad rujira__inner--pad-tight">
          <div className="rujira__inner">
            <h1 className="h1">{t("title")}</h1>
            <PageIntro className="fs-20 lh-28 mw-md-100 balance">
              {Description}
            </PageIntro>

            <div className="flex wrap ai-c gap-1 mt-4 algorithmic__actions">
              <AlgorithmicPairPicker
                className="algorithmic__pair w-sm-30"
                selectedPairKey={pickerPairKey}
                initialPairKey={urlPair ?? undefined}
                onSelect={handlePairSelect}
                onPairChange={handlePickerPairChange}
                onUnavailablePairKey={handleUnavailablePairKey}
              />

              <Button
                className="algorithmic__create button--icon-right"
                disabled={!createStrategyTo}
                onClick={() => createStrategyTo && navigate(createStrategyTo)}
                label={t("createStrategy")}>
                <Icons.AngleRight />
              </Button>

              <Button
                as="a"
                href={toRoot("/product-guides/automated-trading")}
                className="algorithmic__learn button--outline button--grey button--icon-right"
                label={tCommon("learnMore")}>
                <Icons.External />
              </Button>
            </div>

            <div className="flex dir-c gap-2 mt-5">
              <div className="h4 mb-0">{t("leaderboard")}</div>
              <Leaderboard
                pair={leaderboardPair}
                onPairChange={handleLeaderboardPairChange}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
